/**
 * Welcome — the first screen a new user sees.
 *
 * Built to docs/references/02-welcome-screen.png (portrait) and
 * docs/references/05-welcome-dark-landscape.png (landscape, dark).
 *
 * Those two references are the same screen at two shapes, and that is the
 * point: in portrait it stacks, and from a tablet width upward the
 * illustration moves beside the copy rather than above it. A single narrow
 * column centred in a laptop window — which is what this was — looks like a
 * phone screenshot dropped on a desktop.
 *
 * The layout follows the references. The colour and the artwork do not —
 * see the note at the top of styles/theme.css.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ZivDoodles from '../components/brand/ZivDoodles.jsx'
import ThemeToggle from '../components/ui/ThemeToggle.jsx'
import './Onboarding.css'

/** Shipping English only for now; the picker is here so the slot exists. */
const LANGUAGES = [
  { code: 'en',  label: 'English' },
  { code: 'pcm', label: 'Nigerian Pidgin' },
  { code: 'ha',  label: 'Hausa' },
  { code: 'yo',  label: 'Yorùbá' },
  { code: 'ig',  label: 'Igbo' },
  { code: 'fr',  label: 'Français' },
  { code: 'es',  label: 'Español' },
  { code: 'ar',  label: 'العربية' }
]

export default function Onboarding () {
  const navigate = useNavigate()
  const [language, setLanguage] = useState('en')

  return (
    <main className="onb">
      <ThemeToggle className="onb__theme" />

      <div className="onb__art">
        <ZivDoodles />
      </div>

      {/* Copy and actions share a column so the page becomes two columns, not
          three, when the illustration moves to the side. */}
      <div className="onb__panel">
        <div className="onb__copy">
          <h1 className="onb__title">Welcome to ZIvapp</h1>

          <p className="onb__legal">
            Read our <a href="/privacy">Privacy Policy</a>. Tap “Agree and continue”
            to accept the <a href="/terms">Terms of Service</a>.
          </p>
        </div>

        <div className="onb__actions">
          <button
            type="button"
            className="onb__agree"
            onClick={() => navigate('/login')}
          >
            Agree and continue
          </button>

          <div className="onb__lang">
            <svg className="onb__lang-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path
                d="M3 12h18M12 3c2.5 2.6 2.5 15.4 0 18M12 3c-2.5 2.6-2.5 15.4 0 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>

            {/* A native select: it gets the platform's own picker, which is
                both the familiar control and the accessible one. */}
            <select
              className="onb__lang-select"
              value={language}
              onChange={e => setLanguage(e.target.value)}
              aria-label="App language"
            >
              {LANGUAGES.map(l => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>

            <svg className="onb__lang-caret" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </div>
    </main>
  )
}
